import type { ComputedRef, Ref } from 'vue'
import { storeToRefs } from 'pinia'
import type {
    AddToCartReadyToGoRingInput,
    ProductAnalyticsFragment,
    ReadyToGoRingFragment,
    RingSizeInput,
} from '@/graphql/api'
import {} from '@/graphql/api'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import {
    createResponseAndPath,
    type ErrorCollectionWithTraces,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useHandleCta } from '../composables/useHandleCta'
import { useHandleAddToCartSuccess } from '../composables/useHandleAddToCartSuccess'
import { getUri } from '@/helpers/getCurrentUri'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useAddToCartReadyToGoRingExistingCustomerMutation } from '@/graphql/api/cart/ready-to-go/mutations/addToCartReadyToGoRingExistingCustomer.mutation.generated.ts'
import { useAddToCartReadyToGoRingNewCustomerMutation } from '@/graphql/api/cart/ready-to-go/mutations/addToCartReadyToGoRingNewCustomer.mutation.generated.ts'
import { logUnexpectedError } from '@/helpers'

export interface ReadyToGoAddToCart {
    isEnquiryModalOpen: Ref<boolean>
    closeEnquiryModal(): void
    submitNewCustomer(successCallback: () => void): void
    fetchingNewCustomer: Ref<boolean>
    errors: ComputedRef<ErrorCollectionWithTraces>
    fetchingExistingCustomer: Ref<boolean>
    handleAddToCart(): void
}

export function useReadyToGoAddToCart(
    ring: Readonly<
        Ref<
            | (ReadyToGoRingFragment & ProductAnalyticsFragment)
            | undefined
            | null
        >
    >,
    handleRingSizeNotSelected: () => RingSizeInput | null
): ReadyToGoAddToCart {
    const commissionJunction = getCommissionJunctionInput()

    const { prepare } = useHandleAddToCartSuccess(ring)
    const isAdmin = useIsAdmin()
    const getInput = (): AddToCartReadyToGoRingInput | null => {
        if (!ring.value) {
            logUnexpectedError('Could not get input', {
                ring: ring.value,
            })

            return null
        }
        const uri = getUri()

        const recommendedRingSizeId = !ring.value.isReadyToShip
            ? handleRingSizeNotSelected()?.recommendedRingSizeId
            : undefined

        if (recommendedRingSizeId === null) {
            return null
        }

        if (!uri) {
            logUnexpectedError('Could not get input', {
                uri,
            })

            return null
        }

        return {
            catalogueReadyToGoRingCode: ring.value.catalogueCode,
            recommendedRingSizeId,
            uri,
            commissionJunction,
        }
    }

    const existingCustomerResponse =
        useAddToCartReadyToGoRingExistingCustomerMutation()
    const {
        executeMutation: mutationExistingCustomer,
        fetching: fetchingExistingCustomer,
    } = existingCustomerResponse
    const submitExistingCustomer = () => {
        const input = getInput()

        if (!input) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationExistingCustomer({
            input,
        }).then(({ error }) => {
            handleAddToCartSuccess({
                error,
                data: {
                    customer: undefined,
                },
            })
        })
    }

    const newCustomerResponse = useAddToCartReadyToGoRingNewCustomerMutation()
    const {
        executeMutation: mutationNewCustomer,
        fetching: fetchingNewCustomer,
    } = newCustomerResponse

    const { customerRegistrationInput } = storeToRefs(
        useCustomerRegistrationInputStore()
    )

    const submitNewCustomer = (successCallback: () => void) => {
        const input = getInput()
        const customer = customerRegistrationInput.value

        if (!input || !customer) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationNewCustomer({
            input,
            customer,
            isConsultant: isAdmin.value,
        }).then(({ error, data: response }) => {
            const data = response?.addToCartReadyToGoRingNewCustomer

            handleAddToCartSuccess({
                error,
                data,
            })

            if (data?.customer.login) {
                successCallback()
            }
        })
    }

    const errors = useLastResponseErrorAtPath([
        createResponseAndPath(
            existingCustomerResponse,
            'addToCartReadyToGoRingExistingCustomer'
        ),
        createResponseAndPath(
            newCustomerResponse,
            'addToCartReadyToGoRingNewCustomer'
        ),
    ])

    const { isEnquiryModalOpen, closeEnquiryModal, handleAddToCart } =
        useHandleCta(submitExistingCustomer, handleRingSizeNotSelected)

    return {
        isEnquiryModalOpen,
        closeEnquiryModal,
        submitNewCustomer,
        fetchingNewCustomer,
        errors,
        fetchingExistingCustomer,
        handleAddToCart,
    }
}
